import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchFilteredPrices } from "../CoTrader/Utilities/CoTraderUtils";
import {
  getPriceOverlayDateRange,
  getSinglePriceOverlayTarget,
} from "./DashboardUtils";

const getStoredPriceOverlayPreference = (storageKey) => {
  try {
    return localStorage.getItem(storageKey) !== "false";
  } catch {
    return true;
  }
};

const useDashboardPriceOverlay = ({
  enabled = false,
  dashboardChartData,
  filterDate,
  storageKey = "product-dashboard-price-overlay_default_dashboard",
}) => {
  const [overlayPreference, setOverlayPreference] = useState(() => ({
    storageKey,
    enabled: getStoredPriceOverlayPreference(storageKey),
  }));
  const [priceOverlayData, setPriceOverlayData] = useState(null);
  const [isPriceOverlayLoading, setIsPriceOverlayLoading] = useState(false);
  const target = useMemo(
    () => getSinglePriceOverlayTarget(dashboardChartData),
    [dashboardChartData],
  );
  const canShowPriceOverlay = Boolean(enabled && target);
  const showPriceOverlay = Boolean(
    overlayPreference.storageKey === storageKey &&
      overlayPreference.enabled &&
      canShowPriceOverlay
  );

  useEffect(() => {
    setOverlayPreference({
      storageKey,
      enabled: getStoredPriceOverlayPreference(storageKey),
    });
  }, [storageKey]);

  const setShowPriceOverlay = useCallback(
    (nextValue) => {
      const nextEnabled = Boolean(nextValue);
      setOverlayPreference({ storageKey, enabled: nextEnabled });

      try {
        localStorage.setItem(storageKey, String(nextEnabled));
      } catch {
        // Keep the preference for this session if browser storage is unavailable.
      }
    },
    [storageKey]
  );

  useEffect(() => {
    let isCurrent = true;

    if (!showPriceOverlay || !canShowPriceOverlay || !target) {
      setPriceOverlayData(null);
      setIsPriceOverlayLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    const { startDate, endDate } = getPriceOverlayDateRange(filterDate);
    setPriceOverlayData(null);
    setIsPriceOverlayLoading(true);

    void fetchFilteredPrices({
      symbol: target.priceTicker,
      setPricesData: (data) => {
        if (isCurrent) {
          setPriceOverlayData({ ticker: target.priceTicker, data });
        }
      },
      startDate,
      endDate,
      useUtcDateTimeRange: true,
      intervalOverride: "1d",
    }).finally(() => {
      if (isCurrent) {
        setIsPriceOverlayLoading(false);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [canShowPriceOverlay, filterDate, showPriceOverlay, target]);

  const priceOverlay = useMemo(() => {
    if (
      !showPriceOverlay ||
      !target ||
      priceOverlayData?.ticker !== target.priceTicker ||
      !Array.isArray(priceOverlayData?.data) ||
      priceOverlayData.data.length === 0
    ) {
      return null;
    }

    return {
      ticker: target.priceTicker,
      label: target.label,
      data: priceOverlayData.data,
    };
  }, [priceOverlayData, showPriceOverlay, target]);

  return {
    canShowPriceOverlay,
    isPriceOverlayLoading,
    priceOverlay,
    priceOverlayTicker: target?.priceTicker || "",
    showPriceOverlay,
    setShowPriceOverlay,
  };
};

export default useDashboardPriceOverlay;
